import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import SPECIALTY_DATA from './specialtyData';

const SpecialtyPage = () => {
  const { slug } = useParams();
  const data = SPECIALTY_DATA[slug];
  const [openFaq, setOpenFaq] = useState(null);

  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">Page not found</h1>
        <p className="text-gray-500 mb-8">The specialty you're looking for doesn't exist.</p>
        <Link to="/" className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold">
          Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Nav Bar */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="flex justify-between items-center px-6 py-3">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-bold">S</span>
            </div>
            <span className="text-lg font-semibold text-gray-800">Sowmya Mind Care</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className={`bg-gradient-to-r ${data.heroColor} text-white py-16 px-6`}>
        <div className="max-w-4xl mx-auto">
          <p className="text-sm font-medium uppercase tracking-widest opacity-80 mb-2">Sowmya Mind Care</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{data.title}</h1>
          <p className="text-xl opacity-90">{data.subtitle}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">

        {/* Overview */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 pb-2 border-b border-gray-200">Overview</h2>
          <p className="text-gray-700 leading-relaxed text-lg">{data.overview}</p>
        </section>

        {/* Conditions Treated */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 pb-2 border-b border-gray-200">Conditions Treated</h2>
          <div className="space-y-4">
            {data.conditions.map((condition, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{condition.name}</h3>
                <p className="text-gray-600 leading-relaxed">{condition.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Treatment Approaches */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 pb-2 border-b border-gray-200">Treatment Approaches</h2>
          <ul className="space-y-3">
            {data.treatments.map((treatment, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="mt-1 flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">{index + 1}</span>
                <span className="text-gray-700">{treatment}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* When to Seek Help */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 pb-2 border-b border-gray-200">When to Seek Help</h2>
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-6">
            <ul className="space-y-3">
              {data.whenToSeek.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <svg className="mt-1 flex-shrink-0 w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQs */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 pb-2 border-b border-gray-200">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {data.faqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  className="w-full text-left px-6 py-4 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-semibold text-gray-800 pr-4">{faq.q}</span>
                  <svg
                    className={`flex-shrink-0 w-5 h-5 text-blue-600 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFaq === index && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-blue-700 to-indigo-800 text-white rounded-2xl p-10 text-center">
          <h2 className="text-2xl font-bold mb-3">Ready to take the next step?</h2>
          <p className="opacity-90 mb-6 text-lg">
            Dr. Naga Sowmya Garapati offers in-person and online consultations at Sowmya Mind Care, JP Nagar, Bengaluru.
          </p>
          <Link
            to="/#contact"
            className="inline-block bg-white text-blue-700 font-semibold px-8 py-3 rounded-lg hover:bg-blue-50 transition-colors mr-4"
          >
            Contact Us
          </Link>
          <Link
            to="/#specialties"
            className="inline-block border border-white text-white font-semibold px-8 py-3 rounded-lg hover:bg-white hover:text-blue-700 transition-colors"
          >
            All Specialties
          </Link>
        </section>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-6 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-400">
            © {new Date().getFullYear()} Sowmya Mind Care. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SpecialtyPage;
